import { useEffect, useState, useCallback, useMemo, Fragment } from 'react';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ETIQUETA_DE_MODULO } from '@/components/layout/modulos';
import { AppLayout } from '@/components/layout/app-layout';
import { ProformaImportPanel } from '@/features/trade/components/proforma-import-panel';
import { QuotePdfButton } from '@/features/trade/components/quote-pdf-button';
import {
  tabAvailability,
  resolveTab,
  type OrderTabKey,
  type TabAvailability,
} from '../utils/order-tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  ArrowLeft,
  Globe,
  Package,
  Box,
  DollarSign,
  FileText,
  MapPin,
  History,
  Calendar,
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  XCircle,
  CheckCircle2,
  Link2,
  Lock,
  Ship,
  type LucideIcon,
} from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import axiosInstance from '@/lib/api/axios-instance';
import axios from 'axios';
import { useImportTrackingStore } from '../store/import-tracking-store';
import { ImportStatusBadge } from '../components/import-status-badge';
import { MultimodalJourney } from '../components/multimodal-journey';
import { motivoParaNoCambiarEstado, motivoParaNoEmitirDocumento } from '../utils/order-state-rules';
import { importTrackingService } from '../services/import-tracking-service';
import { ShipmentTimeline } from '../components/shipment-timeline';
import { CostBreakdownCard } from '../components/cost-breakdown-card';
import { DocumentUploader } from '../components/document-uploader';
import { ContainersTab } from '../components/containers-tab';
import {
  useCommercialSummary,
  CommercialLegsTable,
  CommercialServicesTable,
  CommercialTotalsCard,
} from '../components/commercial-tab';
import { TrackingEventFormDialog } from '../components/tracking-event-form-dialog';
import { ProductFormDialog } from '../components/product-form-dialog';
import { LegFormDialog } from '../components/leg-form-dialog';
import { ShareClientDialog } from '@/features/public-tracking/components/share-client-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type {
  ImportDetailTab,
  ImportOrderItem,
  ImportOrderLeg,
  ImportOrderStatus,
} from '../types/import-tracking.types';

// ── Helpers ──────────────────────────────────────────────────────

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-BO', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function formatMoney(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('es-BO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

// ── Status Flow ─────────────────────────────────────────────────
// Solo manejamos estados de ciclo de vida a nivel de orden. El detalle
// operativo (embarque, aduana, etc) vive en cada tramo (ImportOrderLeg).
// Mantenemos los enums SHIPPED/CUSTOMS/CUSTOMS_HOLD en el tipo para que
// órdenes viejas con esos estados sigan renderizando, pero no se ofrecen
// como opción nueva en la UI.

const STATUS_FLOW: Partial<Record<ImportOrderStatus, { next: ImportOrderStatus; label: string }>> =
  {
    DRAFT: { next: 'CONFIRMED', label: 'Confirmar Orden' },
    CONFIRMED: { next: 'IN_TRANSIT', label: 'Marcar en tránsito' },
    IN_TRANSIT: { next: 'DELIVERED', label: 'Marcar Entregada' },
  };

const ALL_STATUSES: { value: ImportOrderStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Borrador' },
  { value: 'CONFIRMED', label: 'Confirmada' },
  { value: 'IN_TRANSIT', label: 'En tránsito' },
  { value: 'DELIVERED', label: 'Entregada' },
  { value: 'CANCELLED', label: 'Cancelada' },
];

function StatusActions({
  currentStatus,
  onStatusChange,
}: {
  currentStatus: ImportOrderStatus;
  onStatusChange: (newStatus: ImportOrderStatus) => Promise<void>;
}) {
  const [loading, setLoading] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const flow = STATUS_FLOW[currentStatus];
  const isFinal = currentStatus === 'DELIVERED' || currentStatus === 'CANCELLED';

  const handleChange = (newStatus: ImportOrderStatus, actionLabel: string) => {
    confirm({
      title: 'Confirmar acción',
      description: `¿Confirmar acción: ${actionLabel}?`,
      onConfirm: async () => {
        setLoading(true);
        try {
          await onStatusChange(newStatus);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  if (isFinal) return null;

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Primary action button */}
        {flow && (
          <Button size="sm" disabled={loading} onClick={() => handleChange(flow.next, flow.label)}>
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            {flow.label}
          </Button>
        )}

        {/* Dropdown with more options */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" disabled={loading}>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => handleChange('CANCELLED', 'Cancelar Orden')}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Cancelar Orden
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Cambio directo
            </DropdownMenuLabel>
            {/*
              El cambio directo no ofrece los estados que saltean la
              confirmación. Es un escape legítimo para ponerse al día cuando
              nadie fue marcando los estados, pero desde un borrador solo se sale
              confirmando o cancelando: la confirmación es la misma puerta que
              habilita el enlace del cliente y los documentos de embarque.
            */}
            {ALL_STATUSES.filter(
              (s) => s.value !== currentStatus && !motivoParaNoCambiarEstado(currentStatus, s.value)
            ).map((s) => (
              <DropdownMenuItem
                key={s.value}
                onClick={() => handleChange(s.value, `Cambiar a ${s.label}`)}
              >
                {s.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <ConfirmDialog />
    </>
  );
}

// ── Page ─────────────────────────────────────────────────────────

/**
 * Pestaña del detalle, con o sin candado.
 *
 * El `title` en un `<button disabled>` **no se muestra nunca**: el navegador no
 * emite eventos de mouse sobre un botón deshabilitado, así que el tooltip no
 * llegaba a aparecer. El motivo va en un `<span>` que envuelve al botón, que sí
 * los recibe. Y el candado hace que la traba se vea sin tener que descubrirla.
 */
function GatedTab({
  value,
  icon: Icon,
  label,
  count,
  gate,
}: {
  value: string;
  icon: LucideIcon;
  label: string;
  count?: number;
  gate?: TabAvailability;
}) {
  const trigger = (
    <TabsTrigger
      value={value}
      disabled={gate ? !gate.enabled : false}
      className="items-center gap-1.5 disabled:opacity-45"
    >
      {gate && !gate.enabled ? (
        <Lock className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <Icon className="h-4 w-4" aria-hidden="true" />
      )}
      {label}
      {count !== undefined && count > 0 && (
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">
          {count}
        </span>
      )}
    </TabsTrigger>
  );

  if (!gate || gate.enabled) return trigger;

  return (
    <span title={gate.reason ?? undefined} className="flex cursor-not-allowed">
      {trigger}
    </span>
  );
}

export function ImportOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [showTrackingForm, setShowTrackingForm] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ImportOrderItem | null>(null);
  const [legs, setLegs] = useState<ImportOrderLeg[] | null>(null);
  const [legDialogState, setLegDialogState] = useState<
    | { open: false }
    | { open: true; mode: 'create' }
    | { open: true; mode: 'edit'; leg: ImportOrderLeg }
  >({ open: false });
  const [generatingDoc, setGeneratingDoc] = useState(false);
  const [shippingBannerDismissed, setShippingBannerDismissed] = useState(
    () => !!localStorage.getItem('shippingDocsBannerDismissed')
  );
  const { toast } = useToast();
  const { confirm, ConfirmDialog } = useConfirmDialog();

  /**
   * La pestaña vive en la URL, no solo en memoria.
   *
   * Sin esto, `?tab=containers` no hacía nada: se podía navegar a esa URL y caer
   * en General. Eso rompía el botón "Crear otro contenedor" del armado, que
   * justamente lleva ahí — y hacía imposible compartir el enlace de una pestaña
   * concreta de una orden.
   *
   * `replace: true` para no llenar el historial: pasar por cinco pestañas no
   * debería requerir cinco "atrás" para volver a la lista.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  // Se abrió desde Cotizaciones (`/trade/cotizaciones/:id`): «Volver» lleva ahí.
  const vieneDeCotizaciones = useLocation().pathname.includes('/trade/cotizaciones/');
  const activeDetailTab = (searchParams.get('tab') as ImportDetailTab | null) ?? 'general';
  const setActiveDetailTab = useCallback(
    (tab: ImportDetailTab) => {
      const next = new URLSearchParams(searchParams);
      if (tab === 'general') next.delete('tab');
      else next.set('tab', tab);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const {
    currentOrder,
    isLoadingOrder,
    fetchOrder,
    updateOrder,
    // Items
    orderItems,
    isLoadingItems,
    fetchOrderItems,
    deleteOrderItem,
    // Costs
    costBreakdown,
    isLoadingCosts,
    fetchCostBreakdown,
    // Documents
    documents,
    isLoadingDocuments,
    fetchDocuments,
    uploadDocument,
    deleteDocument,
    // Tracking
    trackingEvents,
    isLoadingTracking,
    fetchTrackingEvents,
    // Audit
    auditLogs,
    isLoadingAuditLogs,
    fetchAuditLogs,
    // Convert quote
    convertToOrder,
  } = useImportTrackingStore();

  useEffect(() => {
    if (id) {
      fetchOrder(id);
    }
  }, [id]);

  const fetchLegs = useCallback(async () => {
    if (!id) return;
    try {
      const data = await importTrackingService.getOrderLegs(id);
      setLegs(data);
    } catch (err) {
      console.error('Error fetching legs:', err);
      setLegs([]);
    }
  }, [id]);

  /**
   * Después de mover un tramo hay que recargar **la orden también**.
   *
   * 🔴 Se recargaban solo los tramos. Pero avanzar un tramo ahora adelanta el
   * estado de la orden y escribe un movimiento, así que la tarjeta cambiaba a
   * "En curso" mientras el paso a paso de arriba seguía diciendo "Confirmada" y
   * Seguimiento seguía vacío. Los datos estaban bien en el servidor; la
   * pantalla mostraba tres versiones distintas de la misma orden hasta que
   * alguien recargara.
   */
  const fetchLegsYOrden = useCallback(async () => {
    if (!id) return;
    await Promise.all([fetchLegs(), fetchOrder(id), fetchTrackingEvents(id)]);
  }, [id, fetchLegs, fetchOrder, fetchTrackingEvents]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    importTrackingService
      .getOrderLegs(id)
      .then((data) => {
        if (!cancelled) setLegs(data);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Error fetching legs:', err);
          setLegs([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  /**
   * Tramos que pueden emitir documento de embarque (HBL marítimo, AWB aéreo).
   *
   * ⚠ **Vacío hasta que la orden está confirmada.** Un HBL es el título de
   * propiedad de la carga: no existe hasta que hay carga embarcada. Ofrecer
   * "Generar documento" antes invita a emitir un papel de algo que todavía nadie
   * compró — y si alguien lo manda, es un documento con valor legal por una venta
   * que no se hizo.
   *
   * 🔴 Antes esto solo excluía `QUOTE`, con un comentario que decía que en
   * `DRAFT` "el cliente ya aceptó". Es lo contrario de lo que dice el resto del
   * sistema: `DRAFT` es justamente el estado en que **todavía no** aceptó, y por
   * eso ahí tampoco se pueden mover los tramos ni compartir el enlace. Además no
   * excluía `CANCELLED`, así que se podía emitir el título de una carga que se
   * canceló.
   */
  const eligibleShippingLegs = useMemo(() => {
    if (!legs || !currentOrder || motivoParaNoEmitirDocumento(currentOrder.status)) return [];
    return legs
      .filter(
        (l) =>
          l.legType === 'INTERNATIONAL' && (l.transportMode === 'SEA' || l.transportMode === 'AIR')
      )
      .sort((a, b) => a.legOrder - b.legOrder);
  }, [legs, currentOrder]);

  const handleGenerateShippingDoc = async (legId: string) => {
    if (!currentOrder) return;
    setGeneratingDoc(true);
    try {
      const res = await axiosInstance.get<string>(
        `/api/import-tracking/orders/${currentOrder.id}/legs/${legId}/shipping-document`,
        { responseType: 'text', transformResponse: (data) => data }
      );

      const contentType = (res.headers['content-type'] as string | undefined) ?? '';
      const text = typeof res.data === 'string' ? res.data : String(res.data ?? '');
      console.log(
        '[shipping-doc] status',
        res.status,
        'content-type',
        contentType,
        'body length',
        text.length
      );

      if (!text || text.length < 100) {
        toast({
          title: 'El servidor devolvió un documento vacío',
          description: `Length: ${text.length}. Revisá logs del backend.`,
          variant: 'destructive',
        });
        return;
      }

      if (!contentType.includes('text/html')) {
        toast({
          title: 'Tipo de contenido inesperado',
          description: `Content-Type: ${contentType}. Esperábamos text/html.`,
          variant: 'destructive',
        });
        return;
      }

      const blob = new Blob([text], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');

      if (!win || win.closed || typeof win.closed === 'undefined') {
        toast({
          title: 'Tu navegador bloqueó la ventana emergente',
          description: 'Permití pop-ups para este sitio y volvé a intentar.',
          variant: 'destructive',
        });
        URL.revokeObjectURL(url);
        return;
      }

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      let title = 'Error generando documento';
      let description: string | undefined =
        error instanceof Error ? error.message : 'Error desconocido';
      if (axios.isAxiosError(error) && error.response) {
        let body: any = error.response.data;
        if (typeof body === 'string') {
          try {
            body = JSON.parse(body);
          } catch {
            /* mantener body string */
          }
        }
        title = body?.error ?? `Error ${error.response.status}`;
        description = body?.missing
          ? `Falta: ${(body.missing as string[]).join(', ')}`
          : typeof body === 'string'
            ? body.slice(0, 200)
            : undefined;
      }
      toast({ title, description, variant: 'destructive' });
    } finally {
      setGeneratingDoc(false);
    }
  };

  const handleDismissShippingBanner = () => {
    localStorage.setItem('shippingDocsBannerDismissed', '1');
    setShippingBannerDismissed(true);
  };

  const handleAddLeg = () => setLegDialogState({ open: true, mode: 'create' });

  const handleEditLeg = (legId: string) => {
    if (!legs) return;
    const leg = legs.find((l) => l.id === legId);
    if (!leg) return;
    setLegDialogState({ open: true, mode: 'edit', leg });
  };

  const handleDeleteLeg = (legId: string) => {
    confirm({
      title: 'Eliminar tramo',
      description: 'Los eventos vinculados quedarán sin tramo asignado. ¿Continuar?',
      variant: 'destructive',
      confirmText: 'Eliminar tramo',
      onConfirm: async () => {
        try {
          await importTrackingService.deleteLeg(legId);
          toast({ title: 'Tramo eliminado' });
          fetchLegs();
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Error al eliminar tramo';
          toast({ title: 'Error', description: msg, variant: 'destructive' });
        }
      },
    });
  };

  // Load tab data on tab change
  useEffect(() => {
    if (!id) return;
    switch (activeDetailTab) {
      case 'products':
        fetchOrderItems(id);
        break;
      case 'costs':
        fetchCostBreakdown(id);
        break;
      case 'documents':
        fetchDocuments(id);
        break;
      case 'tracking':
        fetchTrackingEvents(id);
        break;
      case 'history':
        fetchAuditLogs(id);
        break;
    }
  }, [activeDetailTab, id]);

  // Loading state
  if (isLoadingOrder) {
    return (
      <AppLayout>
        <div className="p-6 max-w-[1200px] mx-auto space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-96 w-full rounded-lg" />
        </div>
      </AppLayout>
    );
  }

  // Not found
  if (!currentOrder) {
    return (
      <AppLayout>
        <div className="p-6 max-w-[1200px] mx-auto text-center py-16">
          <p className="text-muted-foreground">Orden no encontrada</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/trade/embarques')}>
            Volver a Import/Export
          </Button>
        </div>
      </AppLayout>
    );
  }

  const order = currentOrder;
  // Decidir contenedores: si hay legs cargados, usarlos como fuente de verdad
  // (porque order.transportMode a veces queda con el default 'SEA' aunque los
  // tramos sean AIR/LAND). Si NO hay legs (órdenes legacy), caer en el campo
  // de la orden para no esconder la tab a órdenes existentes con contenedores.
  const showContainers =
    legs && legs.length > 0
      ? legs.some((l) => l.transportMode === 'SEA' || l.transportMode === 'MULTIMODAL')
      : order.transportMode === 'SEA' || order.transportMode === 'MULTIMODAL';

  /**
   * Pestañas progresivas: las que todavía no aplican se muestran atenuadas con
   * el motivo, no se ocultan. Así el usuario sabe que existen y cuándo las va a
   * tener, en vez de encontrarlas vacías sin explicación.
   */
  const tab = (key: OrderTabKey) => tabAvailability(key, order.status);

  // Si la pestaña activa no está disponible para este estado (por ejemplo
  // alguien llegó por un enlace a Documentos de una cotización), se muestra
  // General. Se DERIVA en vez de tocar el estado: mutar durante el render
  // dispara un re-render en cascada.
  const effectiveTab = resolveTab(activeDetailTab, order.status);

  return (
    <AppLayout>
      <div className="p-6 max-w-[1200px] mx-auto space-y-6">
        {/* Shipping docs info banner */}
        {eligibleShippingLegs.length > 0 && !shippingBannerDismissed && (
          <div className="flex items-start justify-between gap-3 rounded-md border border-info-borde bg-info-suave px-4 py-3 text-sm text-info">
            <span>
              Cada tramo internacional emite su propio documento (HBL para marítimo, AWB para
              aéreo). Abrirá en una pestaña nueva — usá Ctrl+P / Cmd+P para imprimir o guardar como
              PDF. Los campos sin información quedan en blanco para completarlos a mano antes de
              enviar al cliente.
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 text-info hover:text-foreground hover:bg-info-suave"
              onClick={handleDismissShippingBanner}
            >
              Entendido
            </Button>
          </div>
        )}

        {/* Back + Header */}
        <div>
          <button
            type="button"
            onClick={() =>
              navigate(vieneDeCotizaciones ? '/trade/cotizaciones' : '/trade/embarques')
            }
            className="mb-3 inline-flex items-center gap-1.5 rounded-md text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {vieneDeCotizaciones ? 'Volver a Cotizaciones' : 'Volver a Import/Export'}
          </button>

          {/* El encabezado de Comercia (artefacto, 19-oct): píldora, número grande y acciones. */}
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0 space-y-3">
              <p
                className={`inline-flex h-8 items-center gap-2 rounded-full pl-1.5 pr-3.5 text-[13px] font-semibold ${ETIQUETA_DE_MODULO.comercia}`}
              >
                <span className="grid h-5 w-5 place-items-center rounded-full bg-black/10">
                  <Ship className="h-3 w-3" aria-hidden="true" />
                </span>
                {order.status === 'QUOTE' ? 'Comercia · Cotización' : 'Comercia · Importación'}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-[34px] font-semibold leading-[1.1] tracking-[-0.025em]">
                  {order.orderNumber}
                </h1>
                <ImportStatusBadge status={order.status} />
              </div>
              <p className="text-[15px] text-muted-foreground">
                {order.freightProvider?.name} - {order.freightProvider?.country}
              </p>
              {order.customer ? (
                <div className="flex items-center gap-2 text-sm mt-1">
                  <span className="text-muted-foreground">Para cliente:</span>
                  <span className="font-medium">{order.customer.name}</span>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground italic mt-1">
                  Sin cliente asignado
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {order.status === 'QUOTE' && (
                <>
                  {/*
                    El PDF va primero: mandárselo al cliente es lo que pasa antes
                    de convertir, no después. Convertir sin haber cotizado al
                    cliente sería el orden al revés.
                  */}
                  <QuotePdfButton
                    orderId={order.id}
                    orderNumber={order.orderNumber}
                    variant="full"
                  />
                  <Button
                    variant="degradado"
                    className="px-5"
                    onClick={() => {
                      confirm({
                        title: 'Convertir a orden',
                        description: '¿Convertir esta cotización en una orden?',
                        confirmText: 'Convertir',
                        onConfirm: async () => {
                          try {
                            await convertToOrder(order.id);
                            toast({
                              title: 'Cotización convertida',
                              description: 'La cotización ahora es una orden',
                            });
                          } catch (err: any) {
                            toast({
                              title: 'Error',
                              description: err.message,
                              variant: 'destructive',
                            });
                          }
                        },
                      });
                    }}
                  >
                    Convertir a orden
                  </Button>
                </>
              )}
              {eligibleShippingLegs.length > 0 ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={generatingDoc}>
                      <FileText className="h-4 w-4 mr-2" />
                      {generatingDoc ? 'Generando…' : 'Generar documento'}
                      <ChevronDown className="h-4 w-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    {eligibleShippingLegs.map((leg) => {
                      const kind = leg.transportMode === 'SEA' ? 'HBL' : 'AWB';
                      const emoji = leg.transportMode === 'SEA' ? '🚢' : '✈️';
                      const route = `${leg.origin} → ${leg.destination}`;
                      return (
                        <DropdownMenuItem
                          key={leg.id}
                          onClick={() => handleGenerateShippingDoc(leg.id)}
                          className="flex flex-col items-start gap-0.5"
                        >
                          <span className="text-sm font-medium">
                            {emoji} {kind} — {route}
                          </span>
                          {leg.provider?.name && (
                            <span className="text-xs text-muted-foreground">
                              {leg.provider.name}
                            </span>
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                /*
                  En una cotización no se muestra nada: un HBL es el título de
                  propiedad de la carga y no existe hasta que hay carga embarcada.
                  Un botón deshabilitado tampoco sirve — su `title` no aparece
                  nunca, porque el navegador no emite eventos de mouse sobre un
                  botón deshabilitado. Era un botón muerto que solo ocupaba lugar.

                  Ya siendo orden, si falta el tramo internacional sí se avisa: ahí
                  el documento debería poder emitirse y no poder es un problema.
                */
                order.status !== 'QUOTE' && (
                  <span
                    className="inline-flex cursor-not-allowed"
                    title="Falta un tramo internacional marítimo o aéreo para poder emitir el documento"
                  >
                    <Button variant="outline" size="sm" disabled>
                      <FileText className="mr-2 h-4 w-4" />
                      Generar documento
                    </Button>
                  </span>
                )
              )}
              {/*
                El enlace del cliente recién cuando la orden está confirmada.

                🔴 Antes se podía compartir un BORRADOR. Y un borrador es
                exactamente eso: una orden que todavía se está armando, sin
                tramos cargados o con precios que van a cambiar. El cliente abría
                el enlace, veía un recorrido a medias, y lo primero que hacía era
                llamar a preguntar.

                Se deja visible pero apagado, con el motivo: esconderlo haría que
                el operador lo busque sin encontrarlo. Y si ya se compartió antes
                —el enlace existe— se puede seguir administrando aunque el estado
                cambie, porque revocarlo tiene que ser posible siempre.
              */}
              {(() => {
                const yaCompartida = Boolean(order.publicTrackingToken);
                const puedeCompartir = yaCompartida || order.status !== 'DRAFT';
                const boton = (
                  <Button
                    variant="outline"
                    disabled={!puedeCompartir}
                    onClick={() => setShowShareDialog(true)}
                  >
                    <Link2 className="w-4 h-4 mr-1.5" />
                    {yaCompartida ? 'Link cliente activo' : 'Compartir con cliente'}
                  </Button>
                );
                return puedeCompartir ? (
                  boton
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex cursor-not-allowed">{boton}</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Confirmá la orden antes de compartirla. Un borrador todavía puede cambiar de
                      ruta y de precio.
                    </TooltipContent>
                  </Tooltip>
                );
              })()}
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Costo Nacionalizado</p>
                <p className="text-xl font-bold text-primary">
                  {order.totalLandedCost > 0
                    ? formatMoney(order.totalLandedCost, order.currency)
                    : 'Pendiente'}
                </p>
              </div>
              <StatusActions
                currentStatus={order.status}
                onStatusChange={async (newStatus) => {
                  try {
                    await updateOrder(order.id, { status: newStatus });
                    /*
                      Se recargan los TRAMOS también, no solo la orden.

                      🔴 Cambiar el estado ahora mueve tramos en el servidor —
                      poner EN TRÁNSITO arranca el primero, ENTREGADA completa
                      los que falten— y la pantalla seguía mostrando los de
                      antes. El usuario apretaba, veía cambiar el rótulo de
                      arriba, y abajo todo igual: parecía que no había pasado
                      nada hasta recargar a mano.
                    */
                    await fetchLegsYOrden();
                    const etiqueta =
                      ALL_STATUSES.find((s) => s.value === newStatus)?.label ?? newStatus;
                    toast({
                      title: 'Estado actualizado',
                      description: `La orden ahora está en estado: ${etiqueta}`,
                    });
                  } catch (err: any) {
                    toast({
                      title: 'Error al cambiar estado',
                      description: err.message,
                      variant: 'destructive',
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Journey Map */}
        <Card>
          <CardContent className="p-6">
            {!Array.isArray(legs) ? (
              <div className="h-48 w-full animate-pulse bg-muted/30 rounded-xl" />
            ) : legs.length > 0 ? (
              <MultimodalJourney
                orderId={order.id}
                legs={legs}
                orderStatus={order.status}
                isReadonly={order.status === 'CANCELLED' || order.status === 'DELIVERED'}
                onEditLeg={handleEditLeg}
                onDeleteLeg={handleDeleteLeg}
                onAddLeg={handleAddLeg}
                onRefresh={fetchLegsYOrden}
              />
            ) : (
              /*
                Antes acá había un segundo diseño del recorrido (`ImportJourneyMap`,
                911 líneas) armado con las fechas a nivel de ORDEN. Eran dos
                diseños distintos para lo mismo, y el de la orden no servía para
                operar: mostraba una barra de progreso que nadie podía avanzar,
                porque el avance real es por tramo.

                Una orden sin tramos no tiene recorrido que mostrar. Se dice y se
                ofrece el primer paso.
              */
              <div className="flex flex-col items-center gap-2 py-8 text-center">
                <MapPin className="h-8 w-8 text-muted-foreground/40" />
                <p className="text-sm font-medium">Esta orden todavía no tiene tramos</p>
                <p className="max-w-md text-xs text-muted-foreground">
                  El recorrido se arma con los tramos: Shanghái → Arica con la naviera, Arica →
                  Santa Cruz con el transportista. Las cotizaciones del Cotizador ya vienen con los
                  suyos cargados.
                </p>
                <Button size="sm" variant="outline" className="mt-1" onClick={handleAddLeg}>
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Agregar el primer tramo
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={effectiveTab} onValueChange={(v) => setActiveDetailTab(v as ImportDetailTab)}>
          {/* En línea, como las pestañas del artefacto; en el teléfono se deslizan. */}
          <TabsList variante="linea">
            {/*
              El orden es el del trabajo, no el alfabético: primero lo que se usa
              siempre (qué es · qué trae · cuánto cuesta), después lo que se
              habilita al convertir en orden, y al final lo que aparece cuando la
              carga se mueve. Las trabadas quedan juntas al final, con candado.
            */}
            <GatedTab value="general" icon={Globe} label="General" />
            <GatedTab
              value="products"
              icon={Package}
              label="Productos"
              count={order._count.items}
            />
            <GatedTab value="costs" icon={DollarSign} label="Costos" />
            {showContainers && (
              <GatedTab
                value="containers"
                icon={Box}
                label="Contenedores"
                gate={tab('containers')}
              />
            )}
            <GatedTab
              value="documents"
              icon={FileText}
              label="Documentos"
              count={order._count.documents}
              gate={tab('documents')}
            />
            <GatedTab value="tracking" icon={MapPin} label="Seguimiento" gate={tab('tracking')} />
            <GatedTab value="history" icon={History} label="Historial" />
          </TabsList>

          {/* ── General Tab ─────────────────── */}
          <TabsContent value="general">
            <GeneralTab order={order} />
          </TabsContent>

          {/*
            Acá estaba la pestaña "Entidades" — consignatario, notificado y
            despachante. Se sacó porque nadie leía esos datos: el generador de
            HBL/AWB toma el consignatario de `order.customer` y deja el shipper
            en `null`, así que se podían cargar y el documento salía igual de
            vacío. Ver el comentario en `utils/order-tabs.ts`.

            `EntitiesTab` y sus endpoints se conservan: el día que el HBL use
            estos datos, la pestaña vuelve con una línea.
          */}

          {/* ── Products Tab ────────────────── */}
          <TabsContent value="products">
            <ProductsTab
              items={orderItems}
              isLoading={isLoadingItems}
              currency={order.currency}
              orderId={order.id}
              onImported={() => {
                fetchOrderItems(order.id);
                fetchOrder(order.id);
              }}
              onAdd={() => {
                setEditingProduct(null);
                setShowProductForm(true);
              }}
              onEdit={(item) => {
                setEditingProduct(item);
                setShowProductForm(true);
              }}
              onDelete={(itemId) => {
                confirm({
                  title: 'Eliminar producto',
                  description: '¿Eliminar este producto de la orden?',
                  variant: 'destructive',
                  confirmText: 'Eliminar',
                  onConfirm: async () => {
                    await deleteOrderItem(order.id, itemId);
                    fetchOrderItems(order.id);
                    fetchOrder(order.id);
                  },
                });
              }}
            />
            <ProductFormDialog
              orderId={order.id}
              open={showProductForm}
              onOpenChange={setShowProductForm}
              item={editingProduct ?? undefined}
              onSuccess={() => {
                fetchOrderItems(order.id);
                fetchOrder(order.id);
              }}
            />
          </TabsContent>

          {/* ── Containers Tab ──────────────── */}
          {showContainers && (
            <TabsContent value="containers">
              <ContainersTab
                orderId={order.id}
                /*
                  Lo que el contenedor no debería volver a pedir. El equipo se
                  eligió al cotizar y la ruta y las fechas están en los tramos.
                */
                defaults={{
                  containerTypeId: order.quotedContainerTypeId ?? null,
                  originPort: legs?.[0]?.origin ?? order.originPort ?? null,
                  destinationPort:
                    legs?.[legs.length - 1]?.destination ?? order.destinationPort ?? null,
                  etd: legs?.[0]?.expectedStart ?? order.estimatedShipDate ?? null,
                  eta: legs?.[legs.length - 1]?.expectedEnd ?? order.estimatedArrivalDate ?? null,
                  sourceLabel: 'De la cotización y los tramos',
                }}
              />
            </TabsContent>
          )}

          {/* ── Costs Tab ───────────────────── */}
          <TabsContent value="costs">
            {legs && legs.length > 0 && (
              <Card className="mb-4">
                <CardHeader>
                  <CardTitle className="text-base">Desglose de Flete por Tramo</CardTitle>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="text-left py-2">#</th>
                        <th className="text-left py-2">Tramo</th>
                        <th className="text-left py-2">Proveedor</th>
                        <th className="text-right py-2">Transporte</th>
                        <th className="text-right py-2">Servicios</th>
                        <th className="text-right py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {legs.map((leg) => {
                        const transport = Number(leg.calculatedAmount ?? 0);
                        const services = (leg.services ?? []).reduce(
                          (s, sv) => s + Number(sv.calculatedAmount ?? 0),
                          0
                        );
                        return (
                          <Fragment key={leg.id}>
                            <tr className="border-t">
                              <td className="py-2 text-muted-foreground">{leg.legOrder}</td>
                              <td className="py-2 font-medium">
                                {leg.origin} → {leg.destination}
                              </td>
                              <td className="py-2 text-muted-foreground">
                                {leg.provider?.name ?? leg.carrier ?? '—'}
                              </td>
                              <td className="py-2 text-right font-mono">
                                {leg.currency} {transport.toFixed(2)}
                              </td>
                              <td className="py-2 text-right font-mono text-muted-foreground">
                                {services > 0 ? `${leg.currency} ${services.toFixed(2)}` : '—'}
                              </td>
                              <td className="py-2 text-right font-mono font-semibold">
                                {leg.currency} {(transport + services).toFixed(2)}
                              </td>
                            </tr>
                            {/*
                              Los recargos, uno por uno.

                              🔴 Antes acá había un total y nada más. El tarifario
                              de ABCD trae el desglose —THC, emisión de BL,
                              apertura de manifiesto, box fee— pero se aplastaba
                              en un único importe al cotizar. Cuando el cliente
                              preguntaba "¿por qué 6.539?", no había con qué
                              contestarle.
                            */}
                            {(leg.services ?? []).map((sv) => (
                              <tr key={sv.id} className="text-[11px] text-muted-foreground">
                                <td />
                                <td className="py-1 pl-4" colSpan={2}>
                                  <span className="mr-1.5 text-muted-foreground/60">└</span>
                                  {sv.name}
                                  {sv.notes?.startsWith('Calculado por Prixia') && (
                                    <span
                                      className="ml-1.5 rounded bg-alerta-suave px-1 py-0.5 text-[9px] text-foreground"
                                      title={sv.notes}
                                    >
                                      calculado
                                    </span>
                                  )}
                                </td>
                                <td />
                                <td className="py-1 text-right font-mono">
                                  {Number(sv.calculatedAmount ?? 0).toFixed(2)}
                                </td>
                                <td />
                              </tr>
                            ))}
                          </Fragment>
                        );
                      })}
                      <tr className="border-t-2 font-bold bg-muted/30">
                        <td colSpan={5} className="py-2 text-right">
                          Flete Total:
                        </td>
                        <td className="py-2 text-right font-mono text-primary">
                          {order.currency} {(order.totalFreight ?? 0).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Explicación de cómo se calculan los costos */}
            <Card className="mb-4 bg-muted/30">
              <CardContent className="pt-4">
                <div className="text-xs space-y-1.5">
                  <p className="font-semibold uppercase tracking-wide text-muted-foreground">
                    Cómo se calculan los costos
                  </p>
                  <p>
                    <span className="font-mono">FOB</span> · suma del valor FOB de los items (qty ×
                    unitFobPrice)
                  </p>
                  <p>
                    <span className="font-mono">Flete</span> · suma de todos los tramos (transporte
                    + servicios) según la lógica de cobro elegida en cada uno (peso/volumen/fijo)
                  </p>
                  <p>
                    <span className="font-mono">Seguro</span> · 1,5 % × (FOB + Flete) — es un
                    supuesto; la prima real la fija la póliza
                  </p>
                  <p>
                    <span className="font-mono">Aranceles</span> · GA de la partida arancelaria de
                    cada producto × su CIF
                  </p>
                  {/*
                    Este texto decía "IVA · 13% × (CIF + Aranceles)" y era la
                    fórmula equivocada. En Bolivia el IVA de importación va "por
                    dentro": la tasa efectiva sobre la base es 13/87 = 14,943 %.
                    El cálculo ya estaba corregido en el servicio, pero el texto
                    que se le muestra al usuario había quedado con la vieja — y
                    ese texto es justamente la transparencia que se pidió.
                  */}
                  <p>
                    <span className="font-mono">IVA</span> · (CIF + Aranceles) × 13/87 = 14,943 % —
                    &quot;por dentro&quot;, Ley N°843 Art. 8. <strong>No</strong> es 13 % sobre la
                    base
                  </p>
                  <p>
                    <span className="font-mono">Internación</span> · 0% por ahora (se sumará vía
                    servicios de tramo categorizados)
                  </p>
                </div>
              </CardContent>
            </Card>

            <CostBreakdownCard
              costs={costBreakdown}
              isLoading={isLoadingCosts}
              currency={order.currency}
            />
          </TabsContent>

          {/* ── Documents Tab ───────────────── */}
          <TabsContent value="documents">
            <DocumentUploader
              documents={documents}
              isLoading={isLoadingDocuments}
              onUpload={(file, type, name) => uploadDocument(order.id, file, type, name)}
              onDelete={(docId) => deleteDocument(order.id, docId)}
              onDownload={(doc) =>
                importTrackingService.downloadDocument(order.id, doc.id, doc.fileName ?? doc.name)
              }
            />
          </TabsContent>

          {/* ── Tracking Tab ────────────────── */}
          <TabsContent value="tracking">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Seguimiento de Envío</CardTitle>
                    <CardDescription>
                      Historial de eventos del envío, desde la orden hasta la entrega
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowTrackingForm(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Registrar Evento
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ShipmentTimeline
                  events={trackingEvents}
                  isLoading={isLoadingTracking}
                  legs={legs ?? []}
                />
              </CardContent>
            </Card>
            <TrackingEventFormDialog
              orderId={order.id}
              // Sin esto el desplegable arranca en "Confirmada" y registrar un
              // movimiento hace retroceder a la orden sin que nadie lo pida.
              currentStatus={order.status}
              open={showTrackingForm}
              onOpenChange={setShowTrackingForm}
              onSuccess={() => {
                fetchTrackingEvents(order.id);
                fetchOrder(order.id);
              }}
            />
          </TabsContent>

          {/* ── History Tab ─────────────────── */}
          <TabsContent value="history">
            <AuditLogTab logs={auditLogs} isLoading={isLoadingAuditLogs} />
          </TabsContent>
        </Tabs>
      </div>

      {legDialogState.open && (
        <LegFormDialog
          open={legDialogState.open}
          onOpenChange={(open) => {
            if (!open) setLegDialogState({ open: false });
          }}
          mode={
            legDialogState.mode === 'create'
              ? { kind: 'create', orderId: order.id }
              : { kind: 'edit', leg: legDialogState.leg }
          }
          onSuccess={() => {
            fetchLegs();
            setLegDialogState({ open: false });
          }}
        />
      )}

      <ShareClientDialog
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        title={`Compartir orden ${order.orderNumber}`}
        description="Generá un link público para que tu cliente vea el estado, ETA y monto sin necesidad de cuenta."
        currentToken={order.publicTrackingToken ?? null}
        currentExpiresAt={order.publicTrackingExpiresAt ?? null}
        onGenerate={async (days) => {
          const result = await importTrackingService.shareOrder(order.id, days);
          await fetchOrder(order.id);
          return { token: result.token, expiresAt: result.expiresAt };
        }}
        onRevoke={async () => {
          await importTrackingService.revokeOrderShare(order.id);
          await fetchOrder(order.id);
        }}
        buildUrl={(t) => `${window.location.origin}/track/import/${t}`}
      />

      <ConfirmDialog />
    </AppLayout>
  );
}

// ── General Tab ──────────────────────────────────────────────────

// Card con los campos del tab "General" de Magaya. Solo renderiza
// si al menos uno está cargado, así no ocupa pantalla cuando la orden
// es vieja y no se completaron. Cada subseción se oculta individualmente.
function ShipmentInfoCard({
  order,
}: {
  order: NonNullable<ReturnType<typeof useImportTrackingStore.getState>['currentOrder']>;
}) {
  const ids = [order.bookingNumber, order.customerReference].some(Boolean);
  const issuance = [order.issuancePlace, order.issuanceDate].some(Boolean);
  const customs = [
    order.deliveryType,
    order.generalOrderDate,
    order.documentationCutoffDate,
    order.declaredValue != null,
  ].some(Boolean);
  const cargo = !!order.cargoDescription;

  if (!ids && !issuance && !customs && !cargo) return null;

  return (
    <Card className="md:col-span-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Información del Embarque
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
        {order.bookingNumber && <InfoRow label="Número de Reserva" value={order.bookingNumber} />}
        {order.customerReference && (
          <InfoRow label="Ref. del Cliente" value={order.customerReference} />
        )}
        {order.issuancePlace && (
          <InfoRow label="Lugar de Realización" value={order.issuancePlace} />
        )}
        {order.issuanceDate && (
          <InfoRow label="Fecha de Realización" value={formatDate(order.issuanceDate)} />
        )}
        {order.deliveryType && <InfoRow label="Tipo de Entrega" value={order.deliveryType} />}
        {order.generalOrderDate && (
          <InfoRow label="Fecha de Orden General" value={formatDate(order.generalOrderDate)} />
        )}
        {order.documentationCutoffDate && (
          <InfoRow
            label="Límite de Documentación"
            value={formatDate(order.documentationCutoffDate)}
          />
        )}
        {order.declaredValue != null && (
          <InfoRow
            label="Valor Declarado"
            value={formatMoney(order.declaredValue, order.currency)}
          />
        )}
        {order.cargoDescription && (
          <div className="sm:col-span-2 pt-1 border-t">
            <p className="text-xs text-muted-foreground mb-1">Descripción de la Carga</p>
            <p className="text-sm whitespace-pre-wrap">{order.cargoDescription}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function GeneralTab({
  order,
}: {
  order: NonNullable<ReturnType<typeof useImportTrackingStore.getState>['currentOrder']>;
}) {
  // Nota: proveedor, fechas de embarque/llegada, modo de transporte y
  // origen/destino se muestran a nivel de cada tramo en la sección RUTA.
  // Aquí sólo viven los datos transversales de la orden.
  const {
    summary,
    isLoading: isLoadingCommercial,
    error: commercialError,
    refetch,
  } = useCommercialSummary(order.id);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Fecha de Orden */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Fecha de Orden
            </CardTitle>
          </CardHeader>
          <CardContent>
            <InfoRow label="Fecha" value={formatDate(order.orderDate)} />
          </CardContent>
        </Card>

        {/* Condiciones Comerciales */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Condiciones Comerciales
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow label="Incoterm" value={order.incoterm} />
            <InfoRow label="Condiciones de Pago" value={order.paymentTerms || '-'} />
            <InfoRow label="Moneda" value={order.currency} />
          </CardContent>
        </Card>

        {/* Información del Embarque (paridad Magaya) — solo si hay al menos un campo cargado */}
        <ShipmentInfoCard order={order} />

        {/* Notas */}
        {order.notes && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Notas</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm whitespace-pre-wrap">{order.notes}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Secciones comerciales ─────────────────────────── */}
      {isLoadingCommercial ? (
        <div className="space-y-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : commercialError ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {commercialError}
        </div>
      ) : summary ? (
        <>
          <CommercialLegsTable summary={summary} onUpdated={refetch} />
          <CommercialServicesTable orderId={order.id} summary={summary} onUpdated={refetch} />
          <CommercialTotalsCard summary={summary} />
        </>
      ) : null}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

// ── Item Cost Breakdown (expandable row) ─────────────────────────

function ItemCostBreakdown({ item, currency }: { item: ImportOrderItem; currency: string }) {
  const costs = [
    { label: 'FOB', value: item.totalFobPrice, color: 'bg-info' },
    { label: 'Flete', value: item.allocatedFreight || 0, color: 'bg-info' },
    { label: 'Seguro', value: item.allocatedInsurance || 0, color: 'bg-ok' },
    { label: 'Arancel', value: item.dutyAmount || 0, color: 'bg-alerta' },
    { label: 'IVA', value: item.allocatedTax || 0, color: 'bg-alerta' },
    { label: 'Internación', value: item.allocatedInternacion || 0, color: 'bg-bloqueo' },
    { label: 'Otros', value: item.allocatedOtherCosts || 0, color: 'bg-purple-500' },
  ];
  const total = item.totalItemLandedCost || costs.reduce((s, c) => s + c.value, 0);
  const nonZeroCosts = costs.filter((c) => c.value > 0);

  return (
    <div className="px-6 py-4 bg-muted/30 border-t">
      {/* Stacked cost bar */}
      <div className="mb-3">
        <div className="flex h-3 rounded-full overflow-hidden">
          {nonZeroCosts.map((c) => (
            <div
              key={c.label}
              className={`${c.color} transition-all`}
              style={{ width: `${total > 0 ? (c.value / total) * 100 : 0}%` }}
              title={`${c.label}: ${formatMoney(c.value, currency)}`}
            />
          ))}
        </div>
      </div>

      {/* Cost grid */}
      <div className="grid grid-cols-4 gap-x-6 gap-y-1.5 text-xs">
        {nonZeroCosts.map((c) => (
          <div key={c.label} className="flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${c.color}`} />
              <span className="text-muted-foreground">{c.label}</span>
              {c.label === 'Arancel' && item.dutyRate != null && (
                <span className="text-muted-foreground">
                  ({(item.dutyRate > 1 ? item.dutyRate : item.dutyRate * 100).toFixed(1)}%)
                </span>
              )}
            </span>
            <span className="font-medium tabular-nums">{formatMoney(c.value, currency)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between font-semibold border-t pt-1.5 col-span-4 sm:col-span-1">
          <span className="text-primary">Total</span>
          <span className="text-primary tabular-nums">{formatMoney(total, currency)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Products Tab ─────────────────────────────────────────────────

function ProductsTab({
  items,
  isLoading,
  currency,
  orderId,
  onAdd,
  onEdit,
  onDelete,
  onImported,
}: {
  items: ReturnType<typeof useImportTrackingStore.getState>['orderItems'];
  isLoading: boolean;
  currency: string;
  orderId: string;
  onAdd: () => void;
  onEdit: (item: ImportOrderItem) => void;
  onDelete: (itemId: string) => void;
  onImported: () => void;
}) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>Productos</CardTitle>
            <div className="flex items-center gap-2">
              <ProformaImportPanel orderId={orderId} onImported={onImported} />
              <Button size="sm" onClick={onAdd}>
                <Plus className="w-4 h-4 mr-1" />
                Agregar Producto
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="text-center py-12">
          <Package className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No hay productos en esta orden</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Subí la proforma del proveedor y se cargan todos de una vez.
          </p>
        </CardContent>
      </Card>
    );
  }

  const totalFob = items.reduce((s, i) => s + i.totalFobPrice, 0);
  /*
    × cantidad. `weightKg` y `volumeCbm` son **por unidad** — el formulario lo
    dice: "Peso por unidad (kg)", "Volumen calculado … por unidad".

    🔴 Antes se sumaban en crudo, así que 40 aires de 15 kg mostraban
    "Peso: 15.0 kg" en vez de 600, y 13 m³ de carga aparecían como 0,33 m³.
    Rotulado como total del embarque, es el número con el que alguien elige el
    contenedor o revisa un tramo cobrado por peso.
  */
  const totalWeight = items.reduce((s, i) => s + (i.weightKg || 0) * i.quantity, 0);
  const totalVolume = items.reduce((s, i) => s + (i.volumeCbm || 0) * i.quantity, 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Productos ({items.length})</CardTitle>
          <div className="flex items-center gap-4">
            <div className="flex gap-4 text-sm text-muted-foreground">
              <span>
                FOB Total:{' '}
                <strong className="text-foreground">{formatMoney(totalFob, currency)}</strong>
              </span>
              <span>
                Peso: <strong className="text-foreground">{totalWeight.toFixed(1)} kg</strong>
              </span>
              <span>
                Volumen: <strong className="text-foreground">{totalVolume.toFixed(2)} m3</strong>
              </span>
            </div>
            <ProformaImportPanel orderId={orderId} onImported={onImported} />
            <Button size="sm" onClick={onAdd}>
              <Plus className="w-4 h-4 mr-1" />
              Agregar Producto
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Descripción</TableHead>
                <TableHead>Código HS</TableHead>
                <TableHead className="text-right">Cantidad</TableHead>
                <TableHead className="text-right">Precio Unit. FOB</TableHead>
                <TableHead className="text-right">Total FOB</TableHead>
                {/*
                  "Peso total": la columna de al lado dice "Total FOB", así que
                  una que diga solo "Peso (kg)" se lee como del mismo tipo — y
                  mostraba el peso de UNA unidad.
                */}
                <TableHead className="text-right">Peso total (kg)</TableHead>
                <TableHead className="text-right">Arancel</TableHead>
                <TableHead className="text-right">Landed Unit.</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => {
                const hasBreakdown = item.totalItemLandedCost != null;
                const isExpanded = expandedRows.has(item.id);
                return (
                  <Fragment key={item.id}>
                    <TableRow
                      key={item.id}
                      className={hasBreakdown ? 'cursor-pointer hover:bg-muted/50' : ''}
                      onClick={() => hasBreakdown && toggleExpand(item.id)}
                    >
                      <TableCell className="px-2">
                        {hasBreakdown && (
                          <ChevronDown
                            className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{item.description}</p>
                          {item.material && (
                            <p className="text-xs text-muted-foreground">
                              {item.material.code} - {item.material.name}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{item.hsCode || '-'}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {item.quantity} {item.unitOfMeasure}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(item.unitFobPrice, currency)}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {formatMoney(item.totalFobPrice, currency)}
                      </TableCell>
                      <TableCell
                        className="text-right tabular-nums"
                        title={
                          item.weightKg
                            ? `${item.weightKg} kg por unidad × ${item.quantity}`
                            : undefined
                        }
                      >
                        {item.weightKg ? (item.weightKg * item.quantity).toFixed(1) : '-'}
                      </TableCell>
                      {/*
                        Un 0 % se muestra como "0 %", no como "-".

                        🔴 "-" significa "falta clasificar". Una partida exenta
                        —portátiles, teléfonos, impresoras— está clasificada y su
                        respuesta es cero: son cosas distintas y quien revisa la
                        orden tiene que poder distinguirlas.
                      */}
                      <TableCell className="text-right tabular-nums">
                        {item.dutyRate != null ? (
                          <span
                            className={item.dutyRate === 0 ? 'font-medium text-ok' : undefined}
                            title={item.dutyRate === 0 ? 'Partida exenta de arancel' : undefined}
                          >
                            {(item.dutyRate > 1 ? item.dutyRate : item.dutyRate * 100).toFixed(1)}%
                          </span>
                        ) : (
                          <span title="Sin partida ni alícuota — el cálculo la estima">
                            sin clasificar
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-primary font-semibold">
                        {item.unitLandedCost != null
                          ? formatMoney(item.unitLandedCost, currency)
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <div
                          className="flex items-center gap-1 justify-end"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => onEdit(item)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => onDelete(item.id)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {isExpanded && hasBreakdown && (
                      <TableRow key={`${item.id}-breakdown`}>
                        <TableCell colSpan={10} className="p-0">
                          <ItemCostBreakdown item={item} currency={currency} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Audit Log Tab ────────────────────────────────────────────────

function AuditLogTab({
  logs,
  isLoading,
}: {
  logs: ReturnType<typeof useImportTrackingStore.getState>['auditLogs'];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (logs.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <History className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No hay cambios registrados</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Historial de Cambios</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {logs.map((log) => (
            <div key={log.id} className="flex items-start gap-3 p-3 rounded-md bg-muted/30">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-semibold text-primary">
                  {log.user.firstName[0]}
                  {log.user.lastName[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm">
                  <span className="font-medium">
                    {log.user.firstName} {log.user.lastName}
                  </span>{' '}
                  <span className="text-muted-foreground">{log.action}</span>
                  {log.field && (
                    <span className="text-muted-foreground">
                      {' '}
                      en <code className="text-xs bg-muted px-1 rounded">{log.field}</code>
                    </span>
                  )}
                </p>
                {(log.previousValue || log.newValue) && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {log.previousValue && (
                      <span className="line-through mr-2">{log.previousValue}</span>
                    )}
                    {log.newValue && <span className="text-foreground">{log.newValue}</span>}
                  </p>
                )}
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {new Date(log.createdAt).toLocaleString('es-BO', {
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default ImportOrderDetailPage;
